import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { Entry, EntryCollection } from 'contentful'
import { RootState } from '~/types/store'

interface CategoryFields {
  slug: string
  title: string
  [key: string]: unknown
}

type ArticleCollection = EntryCollection<Record<string, unknown>>

export interface BlogState {
  articles: ArticleCollection | Record<string, never>
  articlesPerPage: number
  categories: Entry<CategoryFields>[]
}

export function state (): BlogState {
  return ({
  articles: {},
  articlesPerPage: 11,
  categories: []
})
}

export const mutations: MutationTree<BlogState> = {
  SET_ARTICLES (state, data: ArticleCollection) {
    state.articles = data
  },
  SET_CATEGORIES (state, data: Entry<CategoryFields>[]) {
    state.categories = data
  }
}

export const getters: GetterTree<BlogState, RootState> = {
  totalPages (state) {
    if (Object.keys(state.articles).length === 0) return 0
    return Math.ceil((state.articles as ArticleCollection).total / state.articlesPerPage)
  },
  currentPage (state) {
    if (Object.keys(state.articles).length === 0) return 0
    return Math.ceil((state.articles as ArticleCollection).skip / state.articlesPerPage) + 1
  }
}

interface GetArticlesPayload {
  mutate?: boolean
  page?: number
  category?: string | null
}

export const actions: ActionTree<BlogState, RootState> = {
  async getArticles ({ state, commit }, { mutate = true, page = 1, category = null }: GetArticlesPayload) {
    const skipNum = (page - 1) * state.articlesPerPage
    let categoryParams = null
    if (category) {
      categoryParams = {
        'fields.category.sys.contentType.sys.id': 'category',
        'fields.category.fields.slug[in]': category
      }
    }
    const articles = await this.$contentful.getEntries({
      content_type: 'article',
      order: '-fields.publishDate',
      limit: state.articlesPerPage,
      skip: skipNum,
      ...categoryParams
    })
    if (mutate) {
      commit('SET_ARTICLES', articles)
    }
    return articles
  },
  async getCategories ({ commit, dispatch }) {
    const { items } = await this.$contentful.getEntries<CategoryFields>({
      content_type: 'category',
      order: 'fields.title'
    })
    const promises: Promise<EntryCollection<unknown>>[] = []
    items.forEach((item) => {
      promises.push(dispatch('getArticles', { mutate: false, category: item.fields.slug }))
    })
    const results = await Promise.all(promises)
    const finalResults = items.filter((item, index) => results[index].total > 0)
    commit('SET_CATEGORIES', finalResults)
  }
}
